import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import RoastDetailModal from "@/components/RoastDetailModal";
import RoastOverlayModal from "@/components/RoastOverlayModal";
import RoastEditModal from "@/components/RoastEditModal";
import { AlertTriangle, Search, GitCompare, Coffee, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const fmt = (n, d = 2) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d });

export default function RoastProfiles() {
  const { user } = useAuth();
  const isOwner = user?.role === "Owner";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [origins, setOrigins] = useState([]);
  const [filter, setFilter] = useState({ origin_id: "", flagged_only: false, date_from: "", date_to: "", q: "" });
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [overlayIds, setOverlayIds] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const limit = 50;

  useEffect(() => { api.get("/origins").then((r) => setOrigins(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit, skip: page * limit };
    if (filter.origin_id) params.origin_id = filter.origin_id;
    if (filter.flagged_only) params.flagged_only = true;
    if (filter.date_from) params.date_from = filter.date_from;
    if (filter.date_to) params.date_to = filter.date_to;
    if (filter.q && filter.q.trim()) params.q = filter.q.trim();
    api.get("/roast-profiles", { params })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [filter.origin_id, filter.flagged_only, filter.date_from, filter.date_to, filter.q, page, reloadKey]);

  // All filtering happens server-side now (q is sent unconditionally).
  const visible = items;

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else if (next.size < 8) next.add(id);
    setSelected(next);
  };

  return (
    <div data-testid="roasts-page">
      <PageHeader
        eyebrow="Operations"
        title="Roast Profiles"
        description="Every roast pulled from ProRoast. Click a row for full detail. Tick checkboxes (up to 8) to overlay curves."
        actions={selected.size >= 2 && (
          <button onClick={() => setOverlayIds([...selected])} data-testid="compare-button"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">
            <GitCompare className="w-4 h-4" /> Compare {selected.size} curves
          </button>
        )}
      />

      <div className="bg-card border border-border rounded mb-4 overflow-x-auto">
        <div className="min-w-[900px]">
        <div className="flex items-center px-4 py-3 border-b border-border">
          <Search className="w-4 h-4 text-muted-foreground mr-2" />
          <input
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            placeholder="Search by roast number, origin, notes, flags…"
            data-testid="roast-search"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
          />
          <span className="text-xs text-muted-foreground ml-3">
            {visible.length} of {total.toLocaleString("en-ZA")} on this page
          </span>
        </div>

        <div className="grid grid-cols-12 gap-px bg-border" data-testid="header-filters">
          <H cols={1} label="" />
          <H cols={1} label="Roast #" />
          <H cols={2} label="When">
            <DateRange from={filter.date_from} to={filter.date_to}
              onChange={(date_from, date_to) => { setFilter({ ...filter, date_from, date_to }); setPage(0); }} />
          </H>
          <H cols={2} label="Origin">
            <select value={filter.origin_id}
              onChange={(e) => { setFilter({ ...filter, origin_id: e.target.value }); setPage(0); }}
              data-testid="filter-origin"
              className="w-full bg-transparent outline-none text-sm">
              <option value="">All origins</option>
              {origins.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </H>
          <H cols={1} label="Green kg" />
          <H cols={1} label="Roasted kg" />
          <H cols={1} label="Loss %" />
          <H cols={1} label="Time" />
          <H cols={2} label="Flags">
            <label className="inline-flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={filter.flagged_only}
                onChange={(e) => { setFilter({ ...filter, flagged_only: e.target.checked }); setPage(0); }}
                data-testid="filter-flagged" />
              <span>Flagged only</span>
            </label>
          </H>
        </div>

        <div className="divide-y divide-border">
          {loading && <p className="px-4 py-8 text-center text-muted-foreground text-sm">Loading…</p>}
          {!loading && visible.length === 0 && (
            <p className="px-4 py-12 text-center text-muted-foreground text-sm">No roasts match.</p>
          )}
          {!loading && visible.map((r) => {
            const isSel = selected.has(r.id);
            return (
              <div key={r.id} className={`grid grid-cols-12 gap-px py-2.5 hover:bg-secondary/40 text-sm transition-colors ${isSel ? "bg-primary/5" : ""}`}>
                <div className="col-span-1 px-3 flex items-center">
                  <input type="checkbox" checked={isSel} onChange={() => toggle(r.id)}
                    data-testid={`select-${r.id}`} className="cursor-pointer" />
                </div>
                <button onClick={() => setOpenId(r.id)} data-testid={`roast-row-${r.id}`}
                  className={`${isOwner ? "col-span-10" : "col-span-11"} grid ${isOwner ? "grid-cols-10" : "grid-cols-11"} gap-px text-left`}>
                  <C cols={1} mono>#{r.roast_number || "—"}</C>
                  <C cols={2} mono>{r.start_datetime ? new Date(r.start_datetime).toLocaleString("en-ZA") : "—"}</C>
                  <C cols={2}>{r.origin_name}</C>
                  <C cols={1} num>{fmt(r.green_weight_kg)}</C>
                  <C cols={1} num>{fmt(r.roasted_weight_kg)}</C>
                  <C cols={1} num
                    className={r.loss_pct && (r.loss_pct < 8 || r.loss_pct > 22) ? "text-[hsl(31,65%,53%)] font-semibold" : ""}>
                    {r.loss_pct != null ? `${fmt(r.loss_pct, 1)}%` : "—"}
                  </C>
                  <C cols={1} num mono>
                    {r.roast_time_seconds ? `${Math.floor(r.roast_time_seconds / 60)}:${String(r.roast_time_seconds % 60).padStart(2, "0")}` : "—"}
                  </C>
                  <C cols={isOwner ? 1 : 2}>
                    {(r.flags || []).length > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs text-[hsl(31,65%,53%)]">
                        <AlertTriangle className="w-3 h-3" />{r.flags.join(", ")}
                      </span>
                    )}
                    {(r.cupping_count > 0) && (
                      <span className="inline-flex items-center gap-1 text-xs text-primary ml-1.5"
                        title={`${r.cupping_count} linked cupping${r.cupping_count === 1 ? "" : "s"}`}>
                        <Coffee className="w-3 h-3" />×{r.cupping_count}
                      </span>
                    )}
                  </C>
                </button>
                {isOwner && (
                  <div className="col-span-1 px-2 flex items-center justify-end">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditId(r.id); }}
                      data-testid={`roast-edit-${r.id}`}
                      title="Edit roast (admin)"
                      className="p-1.5 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {r.note_preview && (
                  <div className="col-span-12 px-12 -mt-1 pb-1 text-[11px] text-muted-foreground italic truncate" data-testid={`note-${r.id}`}>
                    {r.note_preview}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-4 text-sm">
        <p className="text-muted-foreground">Page {page + 1} of {totalPages}</p>
        <div className="flex gap-2">
          <button disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="page-prev"
            className="px-3 py-1.5 border border-border rounded disabled:opacity-40 hover:bg-secondary">Previous</button>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)} data-testid="page-next"
            className="px-3 py-1.5 border border-border rounded disabled:opacity-40 hover:bg-secondary">Next</button>
        </div>
      </div>

      {openId && <RoastDetailModal roastId={openId} onClose={() => setOpenId(null)} />}
      {editId && <RoastEditModal roastId={editId} onClose={() => setEditId(null)} onSaved={() => setReloadKey((k) => k + 1)} />}
      {overlayIds && <RoastOverlayModal ids={overlayIds} onClose={() => setOverlayIds(null)} />}
    </div>
  );
}

function H({ label, cols, children }) {
  return (
    <div className={`col-span-${cols} bg-secondary px-3 py-2`}>
      <p className="label-tiny mb-1">{label}</p>
      {children}
    </div>
  );
}
function C({ cols, children, num, mono, className = "" }) {
  return (
    <div className={`col-span-${cols} px-3 ${num ? "text-right tabular-nums" : ""} ${mono ? "font-mono text-xs" : ""} ${className}`}>
      {children}
    </div>
  );
}
function DateRange({ from, to, onChange }) {
  return (
    <div className="flex items-center gap-1">
      <input type="date" value={from} onChange={(e) => onChange(e.target.value, to)} data-testid="filter-date-from"
        className="w-full bg-transparent outline-none text-xs font-mono" />
      <span className="text-xs text-muted-foreground">→</span>
      <input type="date" value={to} onChange={(e) => onChange(from, e.target.value)} data-testid="filter-date-to"
        className="w-full bg-transparent outline-none text-xs font-mono" />
    </div>
  );
}
